const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

const YP_REVEAL_CONFIG = [
  { selector: '.yp-trusted__heading', anim: 'animate--fade-in', order: 0 },
  { selector: '.yp-trusted__chip', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-services__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-services__card', anim: 'animate--slide-in', cascade: true },
  { selector: '.yp-cats__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-cats__card', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-stats__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-stats__grid > div', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-stats__globe-wrap', anim: 'animate--fade-in', order: 4 },
  { selector: '.yp-projects__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-projects__card', anim: 'animate--slide-in', cascade: true },
  { selector: '.yp-testimonials__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-cta h2', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-cta p', anim: 'animate--fade-in', order: 1 },
  { selector: '.yp-cta__form', anim: 'animate--scale-in', order: 2 },
  { selector: '.yp-team__head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-team__card', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-about__who-copy', anim: 'animate--slide-right', order: 0 },
  { selector: '.yp-about__stack-item', anim: 'animate--fade-in', cascade: true },
  { selector: '.yp-about__mission', anim: 'animate--fade-in', order: 0 },
  { selector: '.yp-about__journey-head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-about__timeline-item', anim: 'animate--slide-in', cascade: true },
  { selector: '.yp-about__process-head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-about__process-item', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-about__values-head', anim: 'animate--slide-in', order: 0 },
  { selector: '.yp-about__value-item', anim: 'animate--scale-in', cascade: true },
  { selector: '.yp-about__contact', anim: 'animate--slide-in', order: 0 },
  { selector: '.footer-block.grid__item', anim: 'animate--slide-in', cascade: true },
  { selector: '.footer-block--newsletter', anim: 'animate--fade-in', order: 0 },
  { selector: '.product__info-wrapper', anim: 'animate--slide-left', order: 0 },
  { selector: '.product__media-wrapper', anim: 'animate--slide-right', order: 0 },
  { selector: '.collection .product-grid .grid__item', anim: 'animate--scale-in', cascade: true, max: 12 },
];

function bootstrapYpScrollAnimations(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  YP_REVEAL_CONFIG.forEach(({ selector, anim, order, cascade, max }) => {
    const nodes = Array.from(rootEl.querySelectorAll(selector));
    const limited = max ? nodes.slice(0, max) : nodes;

    limited.forEach((el, index) => {
      if (el.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) return;

      el.classList.add(SCROLL_ANIMATION_TRIGGER_CLASSNAME, anim);
      el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

      if (cascade || order !== undefined) {
        el.setAttribute('data-cascade', '');
        el.style.setProperty('--animation-order', cascade ? index : order);
      }
    });
  });
}

function onIntersection(elements, observer) {
  elements.forEach((entry) => {
    const elementTarget = entry.target;

    if (entry.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

        if (elementTarget.hasAttribute('data-cascade') && !elementTarget.style.getPropertyValue('--animation-order')) {
          const siblings = Array.from(
            elementTarget.parentElement?.querySelectorAll(`[data-cascade].${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`) || [],
          );
          const idx = siblings.indexOf(elementTarget);
          if (idx >= 0) elementTarget.style.setProperty('--animation-order', idx);
        }
      }
      observer.unobserve(elementTarget);
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  bootstrapYpScrollAnimations(rootEl);

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.08,
  });

  animationTriggerElements.forEach((element) => {
    if (!element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
      element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    }
    observer.observe(element);
  });
}

function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true },
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, false));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
